import axios, { AxiosResponse } from 'axios'
import { useCallback, useEffect, useState } from 'react'

import useSuspenseGetDetailProfile, {
  TUserData,
  getDetailProfile,
} from '../apis/queries/use-suspense-get-detail-profile'
import useSuspenseGetQueryInfo, {
  TUserQueryInfoResponse,
  getQueryInfo,
} from '../apis/queries/use-suspense-get-query-info'
import {
  setCurrentUserEmail,
  setCurrentUserFullname,
  setCurrentUserId,
  setCurrentUserPhone,
  setCurrentUserUsername,
} from '../store/slice/user-slice'
import { ESpecialPartnerId, TApiResult } from '../types/global.types'

import { useAppDispatch } from './use-app-dispatch'
import { useAppSelector } from './use-app-selector'
import useToast from './use-toast'

const useGetUser = () => {
  const dispatch = useAppDispatch()
  const toast = useToast()
  const { partnerId } = useAppSelector((state) => state.user)

  const isGopay = partnerId === ESpecialPartnerId.GOPAY

  const { data: profileQuery, isLoading: isLoadingProfileQuery } = useSuspenseGetDetailProfile({
    enabled: !isGopay,
  })

  const { data: queryInfoQuery, isLoading: isLoadingQueryInfo } = useSuspenseGetQueryInfo(
    profileQuery?.data?.result?.email as string,
  )

  const [isFetching, setIsFetching] = useState(true)

  const [profile, setProfile] = useState<null | AxiosResponse<TApiResult<TUserData>>>(null)
  const [queryInfo, setQueryInfo] = useState<null | AxiosResponse<
    TApiResult<TUserQueryInfoResponse>
  >>(null)

  const fetchProfile = useCallback(async () => {
    setIsFetching(true)

    try {
      const res = await getDetailProfile()

      setProfile(res)
      setIsFetching(false)
      await fetchQueryInfo(res?.data?.result?.email)
    } catch (error) {
      setIsFetching(false)
      if (axios.isAxiosError(error)) {
        toast.show({
          title: error?.response?.data?.error?.code || 'Error Occurred',
          description: error?.response?.data?.error?.message,
          type: 'error',
          traceId: error?.response?.data?.traceId || '',
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchQueryInfo = async (email: string) => {
    try {
      const res = await getQueryInfo(email)

      setQueryInfo(res)
      setIsFetching(false)
    } catch (error) {
      setIsFetching(false)
      if (axios.isAxiosError(error)) {
        toast.show({
          title: error?.response?.data?.error?.code || 'Error Occurred',
          description: error?.response?.data?.error?.message,
          type: 'error',
          traceId: error?.response?.data?.traceId || '',
        })
      }
    }
  }

  useEffect(() => {
    if (isGopay) fetchProfile()
  }, [fetchProfile, isGopay])

  useEffect(() => {
    if (!!profile) {
      // this should not required if all the user state is already migrated to the server state
      dispatch(setCurrentUserEmail(profile?.data?.result?.email))
      dispatch(setCurrentUserUsername(profile?.data?.result?.email))
      dispatch(setCurrentUserFullname(profile?.data?.result?.fullName))
      dispatch(setCurrentUserPhone(profile?.data?.result?.phoneNumber))
      dispatch(setCurrentUserId(profile?.data?.result?.id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile])

  return {
    profile: isGopay ? profile?.data?.result : profileQuery?.data?.result || null,
    queryInfo: isGopay ? queryInfo?.data?.result : queryInfoQuery?.data?.result || null,
    isFetching: isGopay ? isFetching : isLoadingProfileQuery || isLoadingQueryInfo,
  }
}

export default useGetUser
